import { useEffect, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { useLanguage } from "../context/LanguageContext";
import arrow from "../assets/images/down-arrow.svg"
import activeArrow from "../assets/images/active-down-arrow.svg"
import { fadeDown, staggerContainer } from "../lib/motion"
import extraLogo from "../assets/images/extra-logo.svg"
import { sendFormDataToSlack } from "../utils/slack"
import { sendFormDataToZoho } from "../utils/zoho"

interface props {
    onSuccess: () => void;
    isOpen: boolean;
}
const ArticalFormModal = ({ onSuccess, isOpen }: props) => {
    const { translation, isRTL, lang } = useLanguage();
    const [isCompanySizeOpen, setIsCompanySizeOpen] = useState(false);
    const [formData, setFormData] = useState({name: '', email: '', phoneNumber: '', companyName: '', companySize: '', jobTitle: '', message: ''});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const [slackResult, zohoResult] = await Promise.allSettled([
                sendFormDataToSlack(formData, lang, 'article'),
                sendFormDataToZoho(formData, 'article', lang)
            ]);

            const slackSuccess = slackResult.status === 'fulfilled' && slackResult.value.success;
            const zohoSuccess = zohoResult.status === 'fulfilled' && zohoResult.value.success;

            if (slackSuccess || zohoSuccess) {
                setSubmitStatus('success');
                setFormData({ name: '', email: '', phoneNumber: '', companyName: '', companySize: '', jobTitle: '', message: '' });

                setTimeout(() => {
                    onSuccess();
                    setSubmitStatus('idle');
                }, 2000);
            } else {
                setSubmitStatus('error');
                console.error('فشل إرسال البيانات:', {
                    slack: slackResult.status === 'rejected' ? slackResult.reason : slackResult.value.error,
                    zoho: zohoResult.status === 'rejected' ? zohoResult.reason : zohoResult.value.error
                });
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('حدث خطأ:', error);
        } finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        const close = () => setIsCompanySizeOpen(false)
        document.addEventListener("click", close)
        return () => document.removeEventListener("click", close)
    }, [])

  return (
    <AnimatePresence mode="wait">
        {isOpen && (
            <motion.div
                className="flex items-center justify-center px-5 w-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.4, ease: "easeInOut" } }}
            >
                <motion.form 
                    onSubmit={onSubmit}
                    className="relative w-[20.8rem] lg:w-[29.75rem] bg-[#E4ECEC] rounded-2xl p-8 md:p-10 flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8 shadow-[0_20px_30px_-16px_rgba(0,0,0,0.2)]"
                    initial={{ opacity: 0, y: 16, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -20, scale: 0.95, transition: { duration: 0.5, ease: "easeInOut" } }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    onClick={(e) => e.stopPropagation()}
                >
            {/* Logo */}
            <motion.div className="flex items-center justify-center">
                <div className="flex-col flex">
                    <img src={lang === "ar" ? "/logos/logo-ar.svg" : "/logos/logo.svg"} alt="NowPay" className="h-6 w-auto" />
                    <div className="flex gap-5 justify-end">
                        <img src={extraLogo} alt="eXtra" className="w-6 h-auto"/>
                    </div>
                </div>
            </motion.div>
            <motion.div
                className="flex flex-col gap-4 sm:gap-[1.375rem] lg:gap-8"
                variants={staggerContainer(0.08, 0.04)}
                initial="hidden"
                animate="show"
            >
                <motion.div className="flex flex-col gap-[0.375rem] lg:gap-3 text-center" variants={fadeDown}>
                    <h2 className="text-2xl leading-[100%] font-bold">{translation.articalModalTitle}</h2>
                    <p className="text-base leading-[1.25]">{translation.articalModalDescription}</p>
                </motion.div>
                
                {submitStatus === 'success' && (
                    <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm"
                    >
                        ✅ {translation.formSubmitSuccess}
                    </motion.div>
                )}
                {submitStatus === 'error' && (
                    <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
                    >
                        ❌ {translation.formSubmitError}
                    </motion.div>
                )}

                <motion.div className="flex flex-col gap-3 sm:gap-[1.125rem] md:gap-6 lg:gap-8 max-h-[50vh] pe-2 -me-2" variants={fadeDown}>
                    <div className="flex flex-col gap-1 text-primary">
                        <input
                            type="text"
                            placeholder={translation.formName}
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            required
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 px-3 focus:outline-none text-sm disabled:opacity-50 text-black placeholder:text-gray-400"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        <input
                            type="email"
                            placeholder={translation.formEmail}
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            required
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 px-3 focus:outline-none text-sm disabled:opacity-50 text-black placeholder:text-gray-400"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        <input
                            type="tel"
                            placeholder={translation.formPhoneNumber} 
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                            required
                            pattern="[0-9\+\-\s\(\)]{10,}"
                            title="Please enter a valid phone number"
                            dir={isRTL ? "rtl" : "ltr"}
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 px-3 focus:outline-none text-sm disabled:opacity-50 text-black placeholder:text-gray-400"
                        />
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        <input
                            type="text"
                            placeholder={translation.formCompanyName}
                            value={formData.companyName}
                            onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 px-3 focus:outline-none text-sm disabled:opacity-50 text-black placeholder:text-gray-400"
                        />
                    </div>
                    <div className="flex flex-col gap-1 relative bg-white">
                        <span className={`${lang === "ar" ? "" : "hidden"} pointer-events-none absolute left-2 bottom-1 -translate-y-1/2`}>
                            <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                        </span>
                        <button
                            type="button"
                            onClick={() => setIsCompanySizeOpen((prev) => !prev)}
                            disabled={isSubmitting}
                            className={
                            `text-start w-full border-b border-[#DCDFE1] py-2 px-3 bg-transparent focus:outline-none text-sm disabled:opacity-50 ${formData.companySize ? "text-black" : "text-gray-400"}`
                            }
                        >
                            {formData.companySize || translation.companySize}
                        </button>
                        <span className={`${lang === "en" ? "" : "hidden"} pointer-events-none absolute right-2 bottom-1 -translate-y-1/2`}>
                            <img src={isCompanySizeOpen ? activeArrow : arrow} alt="arrow" />
                        </span>
                        {isCompanySizeOpen && (
                            <div className="absolute bottom-full left-0 mb-2 z-[9999] w-full max-h-[40vh] overflow-y-auto rounded-xl bg-white shadow-2xl border border-gray-200">
                                {["1-50", "51-200", "201-500", "501-1000", "1000+"].map((size) => (
                                    <button
                                        key={size}
                                        type="button"
                                        onClick={() => {
                                            setFormData({ ...formData, companySize: size })
                                            setIsCompanySizeOpen(false)
                                        }}
                                        className={`flex items-center w-full text-left px-4 py-3 text-sm hover:bg-gray-100 transition ${size === formData.companySize && "bg-gray-100 font-semibold"}`}
                                    >
                                        {size}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-1 text-primary">
                        <input 
                            type="text"
                            placeholder={translation.formJobTitle}
                            value={formData.jobTitle}
                            onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                            disabled={isSubmitting}
                            className="w-full border-b border-[#DCDFE1] py-2 px-3 focus:outline-none text-sm disabled:opacity-50 text-black placeholder:text-gray-400"
                        />
                    </div>
                </motion.div>
                <motion.button 
                    className="w-full mx-auto pt-2 lg:pt-3 -mt-2" 
                    type="submit" 
                    variants={fadeDown}
                    disabled={isSubmitting}
                >
                    <div className={`h-10 rounded-[6.25rem] py-6 px-[3.75rem] bg-softGreen hover:bg-[#6BB37F] text-sm font-semibold leading-[100%] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}>
                        {isSubmitting 
                            ? translation.formSubmitting 
                            : translation.articalButton
                        }
                    </div>
                </motion.button>
            </motion.div>
                </motion.form>
            </motion.div>
        )}
    </AnimatePresence>
  )
}

export default ArticalFormModal
